import { RefObject, useEffect, useRef } from 'react';

import { moveFocusCameraWidgetExitFullscreenThenNavigate } from '@/providers/gamepadProvider/gamepadProvider.help.ts';

import {
  createHoldRepeatWhileKeyHeldHandlers,
  type HoldRepeatWhileKeyHeldBinding,
} from './holdRepeatWhileKeyHeld.ts';
import {
  chordSecondaryMatches,
  HOTKEY_CHORD_TIMEOUT_MS,
  type PendingHotkeyChord,
} from './hotkeyChordHelpers.ts';
import {
  clearTimeoutFromRef,
  hotkeyPrimaryOrOptionalCodeMatches,
  isInside,
  isMenuNavigationKeyEvent,
  isTypingTarget,
  keyboardModifiersMatchHotkeyBase,
} from './hotkeyFunctions.ts';
import type { Hotkey } from './hotkeys';
import { CameraControlHotkeyNames } from './hotkeys/types.ts';

type HoldRepeatConfig = {
  intervalMs: number;
  callOnFirstTick: boolean;
  bindings: HoldRepeatWhileKeyHeldBinding[];
};

type UseCameraControlHotkeysParams = {
  scopeRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  onToggleFullscreen: ((element: HTMLDivElement) => void) | undefined;
  /** Opens the camera selector modal (landing-page widget only). */
  onSwitchCamera?: () => void;
  /** Selects the next sibling camera (landing-page widget only). */
  onCycleCamera?: () => void;
  cameraControlHotKeys: Partial<Record<CameraControlHotkeyNames, Hotkey>>;
  /** Pan/tilt (WASD) and winch arrows: repeat on an interval while the key chord is held. */
  holdRepeat?: HoldRepeatConfig;
};

/**
 * Capture-phase keyboard shortcuts for the focused camera stream widget: `cameraControlHotKeys`
 * from `useCameraControlHotKeyDefinitions()` (modifiers + optional two-stroke chords), plus optional
 * hold-to-repeat bindings (`holdRepeat`) that use `setInterval` while a key is held.
 */
export const useCameraControlHotkeys = ({
  scopeRef,
  enabled,
  onToggleFullscreen,
  onSwitchCamera,
  onCycleCamera,
  cameraControlHotKeys,
  holdRepeat,
}: UseCameraControlHotkeysParams) => {
  const pendingChordRef = useRef<PendingHotkeyChord<CameraControlHotkeyNames> | null>(null);
  const chordTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return;

    const holdRepeatHandlers = holdRepeat?.bindings.length
      ? createHoldRepeatWhileKeyHeldHandlers({
          bindings: holdRepeat.bindings,
          intervalMs: holdRepeat.intervalMs,
          callOnFirstTick: holdRepeat.callOnFirstTick,
        })
      : null;

    const handleHotkeyName = (name: CameraControlHotkeyNames) => {
      switch (name) {
        case CameraControlHotkeyNames.FocusNextVideoStream: {
          void moveFocusCameraWidgetExitFullscreenThenNavigate(1);
          return;
        }
        case CameraControlHotkeyNames.FocusPreviousVideoStream: {
          void moveFocusCameraWidgetExitFullscreenThenNavigate(-1);
          return;
        }
        case CameraControlHotkeyNames.ToggleFullScreen:
        case CameraControlHotkeyNames.AlternativeToggleFullScreen: {
          if (!onToggleFullscreen) return;
          const el = scopeRef.current;
          if (!el) return;
          onToggleFullscreen(el as HTMLDivElement);
          return;
        }
        case CameraControlHotkeyNames.SwitchCamera: {
          onSwitchCamera?.();
          return;
        }
        case CameraControlHotkeyNames.CycleCamera: {
          onCycleCamera?.();
          return;
        }
      }
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (!isInside(scopeRef)) return;
      if (isTypingTarget(ev.target)) return;
      if (isMenuNavigationKeyEvent(ev)) return;

      const pending = pendingChordRef.current;
      if (pending && chordSecondaryMatches(ev, pending)) {
        ev.preventDefault();
        ev.stopPropagation();
        clearTimeoutFromRef(chordTimeoutRef);
        pendingChordRef.current = null;

        handleHotkeyName(pending.name);
        return;
      }

      if (pending && !chordSecondaryMatches(ev, pending)) {
        clearTimeoutFromRef(chordTimeoutRef);
        pendingChordRef.current = null;
      }

      if (holdRepeatHandlers && holdRepeatHandlers.onKeyDown(ev)) {
        ev.stopPropagation();
        return;
      }

      if (ev.repeat) return;

      for (const [name, hotkey] of Object.entries(cameraControlHotKeys) as [
        CameraControlHotkeyNames,
        Hotkey,
      ][]) {
        if (!hotkeyPrimaryOrOptionalCodeMatches(hotkey, ev.code)) continue;
        if (!keyboardModifiersMatchHotkeyBase(ev, hotkey)) continue;

        ev.preventDefault();
        ev.stopPropagation();

        if (hotkey.secondary) {
          pendingChordRef.current = { name, hotkey };
          clearTimeoutFromRef(chordTimeoutRef);
          chordTimeoutRef.current = setTimeout(() => {
            pendingChordRef.current = null;
            chordTimeoutRef.current = undefined;
          }, HOTKEY_CHORD_TIMEOUT_MS);
          return;
        }

        handleHotkeyName(name);
        return;
      }
    };

    const onKeyUp = (ev: KeyboardEvent) => {
      if (!holdRepeatHandlers) return;
      if (holdRepeatHandlers.onKeyUp(ev)) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };

    const onBlurWindow = () => {
      holdRepeatHandlers?.dispose();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onBlurWindow);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onBlurWindow);
      clearTimeoutFromRef(chordTimeoutRef);
      pendingChordRef.current = null;
      holdRepeatHandlers?.dispose();
    };
  }, [
    enabled,
    onToggleFullscreen,
    onSwitchCamera,
    onCycleCamera,
    scopeRef,
    cameraControlHotKeys,
    holdRepeat,
  ]);
};
